'use client';

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ProjectAccessDialog from './ProjectAccessDialog';
import { ENTITY_CONFIGS } from './EntityConfigs';
import { FormTextField, FormAutocomplete, FormSelect, FormLabel } from '@/components/common/FormComponents';
import { CustomizingDialogProps, CustomizingRecord, EntityType } from '@/types/customizing';

import { log } from '@/lib/utils/logger';
import { generateMaterialLabel } from '@/lib/analysis/materialLabelGenerator';
import { ProjectSelectionDialog } from '@/components/dialog/ProjectSelectionDialog';
import { ProjectRowType } from '@/components/tables/ProjectGridCore';
import { ApiProjectAccessDto, FileMetaDataConfigDto, MaterialDto, PrivilegeEnum } from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { invalidateCachedOptions } from '@/lib/options/optionsCache';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { InstrumentFileMetadataConfigEditor } from '@/components/fileMetadata/InstrumentFileMetadataConfigEditor';
import {
  createEmptyInstrumentMetadataConfig,
  syncMetadataConfigInstrumentId,
  type InstrumentMetadataKind,
} from '@/lib/fileMetadata/instrumentMetadataConfig';
import { usePrivileges } from '@/hooks/usePrivileges';

function instrumentMetadataKindForEntity(entityType: EntityType): InstrumentMetadataKind | null {
  if (entityType === 'characterizationInstruments') return 'characterization';
  if (entityType === 'synthesisInstruments') return 'synthesis';
  return null;
}

export default function CustomizingEntityDialog({
  open,
  entityType,
  entityConfig,
  record,
  isEdit,
  onSave,
  onClose,
}: CustomizingDialogProps) {
  const [formData, setFormData] = useState<CustomizingRecord>(() => {
    if (record) {
      const initialData = { ...record };
      // TODO: We need to load the project details to get the labels
      // For now, we'll set empty array and let user re-select
      return initialData;
    } else {
      const initialData: CustomizingRecord = { id: '' } as CustomizingRecord;
      entityConfig.fields.forEach((field) => {
        switch (field.type) {
          case 'boolean':
            initialData[field.name] = false;
            break;
          case 'date':
          case 'datetime-local':
            initialData[field.name] = null;
            break;
          case 'multiselect':
          case 'multiselect-autocomplete':
            initialData[field.name] = [];
            break;
          case 'number':
            initialData[field.name] = null;
            break;
          case 'instrument-file-metadata': {
            const kind = instrumentMetadataKindForEntity(entityType);
            if (kind) {
              initialData[field.name] = createEmptyInstrumentMetadataConfig(kind, '');
            }
            break;
          }
          default:
            initialData[field.name] = '';
            break;
        }
      });
      return initialData;
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedProjects, setSelectedProjects] = useState<{ id: number; label: string }[]>([]);
  const [isProjectSelectionOpen, setIsProjectSelectionOpen] = useState(false);
  const [isProjectAccessDialogOpen, setIsProjectAccessDialogOpen] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [isMaterialCreateDialogOpen, setIsMaterialCreateDialogOpen] = useState(false);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { isAuthorized } = usePrivileges();
  const instrumentMetadataKind = instrumentMetadataKindForEntity(entityType);
  const canEditFileMetadata = isAuthorized(PrivilegeEnum.ConfigureFileMetaData, 'update');

  /** When a project type is referenced by projects, code and internal/external are immutable (server-enforced). */
  const isProjectTypeLockedForEdit = entityType === 'projectTypes' && isEdit && Boolean(record?.isUsed);

  // Get all option keys needed by the entity fields
  const optionKeys = useMemo(() => {
    return entityConfig.fields
      .filter((field) => typeof field.options === 'string')
      .map((field) => field.options as OptionKey);
  }, [entityConfig.fields]);

  // Load all needed options
  const { options: selectOptions, loading: selectOptionsLoading } = useSelectOptions(optionKeys);

  // Derive optionsLoaded from dialog state and loading status (entities with no dropdown options skip the fetch)
  const optionsLoaded = open && (optionKeys.length === 0 || !selectOptionsLoading);

  // Compute unsaved changes from form data - derived state using useMemo
  const hasUnsavedChanges = useMemo(() => {
    if (record) {
      // For editing existing records, compare with original values
      return entityConfig.fields.some((field) => {
        const currentValue = formData[field.name as keyof CustomizingRecord];
        const originalValue = record[field.name as keyof CustomizingRecord];

        if (field.type === 'instrument-file-metadata') {
          return JSON.stringify(currentValue ?? null) !== JSON.stringify(originalValue ?? null);
        }
        if (field.type === 'multiselect-autocomplete' || field.type === 'multiselect') {
          const currentArray = Array.isArray(currentValue) ? currentValue : [];
          const originalArray = Array.isArray(originalValue) ? originalValue : [];
          return JSON.stringify(currentArray) !== JSON.stringify(originalArray);
        } else {
          return currentValue !== originalValue;
        }
      });
    } else {
      // For new records, only consider it "unsaved" if user has entered meaningful data
      return entityConfig.fields.some((field) => {
        const value = formData[field.name as keyof CustomizingRecord];

        // Skip auto-generated fields (like labels) from consideration
        if (field.name === 'label' && entityConfig.title === 'Materials') {
          return false; // Don't consider auto-generated labels as user input
        }

        if (field.type === 'multiselect-autocomplete' || field.type === 'multiselect') {
          return Array.isArray(value) && value.length > 0;
        } else if (field.type === 'number') {
          return value !== null && value !== undefined && value !== '';
        } else if (field.type === 'date' || field.type === 'datetime-local') {
          return value !== null && value !== undefined && value !== '';
        } else if (field.type === 'boolean') {
          return value === true; // Only consider true as meaningful change from default false
        } else {
          // For text fields, only consider non-empty strings with actual content
          return typeof value === 'string' && value.trim().length > 0;
        }
      });
    }
  }, [formData, record, entityConfig.fields, entityConfig.title]);

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [fieldName]: value,
      };

      // Auto-generate label for materials when name changes
      if (entityConfig.title === 'Materials' && fieldName === 'name') {
        if (value) {
          newData.label = generateMaterialLabel(value);
        }
      }

      if (fieldName === 'id' && instrumentMetadataKind) {
        newData.metadataConfig = syncMetadataConfigInstrumentId(
          newData.metadataConfig as FileMetaDataConfigDto | undefined,
          instrumentMetadataKind,
          String(value ?? '')
        );
      }

      return newData;
    });

    // Real-time validation for reporting category code
    if (entityConfig.title === 'Reporting Categories' && fieldName === 'code' && !isEdit) {
      const code = value?.trim();
      if (code) {
        const existingCategories = selectOptions.sampleReportingCategory ?? [];
        const duplicateCategory = existingCategories.find((cat: any) => cat.code?.toLowerCase() === code.toLowerCase());
        if (duplicateCategory) {
          setErrors((prev) => ({
            ...prev,
            [fieldName]: `Reporting category with code '${code}' already exists`,
          }));
          return; // Don't clear error if duplicate found
        }
      }
      // Clear error if no duplicate found
      if (errors[fieldName]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
      return; // Don't run the general error clearing below
    }

    // Clear error when user starts typing (for other fields)
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    entityConfig.fields.forEach((field) => {
      if (field.required) {
        const value = formData[field.name];
        // Handle different field types for validation
        if (field.type === 'multiselect-autocomplete' || field.type === 'multiselect') {
          // For multiselect fields, check if array is empty
          if (!Array.isArray(value) || value.length === 0) {
            newErrors[field.name] = `${field.label} is required`;
          }
        } else if (field.type === 'boolean') {
          // false is valid; only reject missing values (do not use !value — it treats false as empty)
          if (value !== true && value !== false) {
            newErrors[field.name] = `${field.label} is required`;
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          // For other field types, use existing validation
          newErrors[field.name] = `${field.label} is required`;
        }
      }

      // Email validation
      if (field.type === 'email' && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.name])) {
          newErrors[field.name] = 'Please enter a valid email address';
        }
      }

      // Number validation
      if (field.type === 'number' && formData[field.name]) {
        if (isNaN(Number(formData[field.name]))) {
          newErrors[field.name] = 'Please enter a valid number';
        }
      }
    });

    // Project types when in use: prefix may only add, not remove
    if (entityType === 'projectTypes' && isEdit && record?.isUsed) {
      const existingPrefixes = String(record.prefix ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const newPrefixes = String(formData.prefix ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const missing = existingPrefixes.filter((p) => !newPrefixes.some((n) => n.toLowerCase() === p.toLowerCase()));
      if (missing.length > 0) {
        newErrors.prefix =
          'When the project type is in use, existing prefixes may not be removed. You may only add new prefixes (comma-separated).';
      }
    }

    // Custom validation for reporting categories: Check for duplicate code
    if (entityConfig.title === 'Reporting Categories' && !isEdit) {
      const code = formData.code?.trim();
      if (code) {
        const existingCategories = selectOptions.sampleReportingCategory ?? [];
        const duplicateCategory = existingCategories.find((cat: any) => cat.code?.toLowerCase() === code.toLowerCase());
        if (duplicateCategory) {
          newErrors.code = `Reporting category with code '${code}' already exists`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showSnackbar('Please fix the highlighted errors before saving.', 'error');
      return;
    }

    try {
      //convert empty strings to nulls so that they get entered into the DB on the back-end correctly
      let submissionData = formData;
      Object.keys(submissionData).forEach((key) => {
        const keyWithType = key as keyof typeof submissionData;
        const value = submissionData[keyWithType];
        if (typeof value === 'string' && value === '') {
          submissionData = { ...submissionData, [keyWithType]: null };
        }
      });
      await onSave(submissionData);
    } catch (error) {
      log.error('Error saving record:', error);
    }
  };

  // Project selection handlers
  const handleProjectSelect = (project: ProjectRowType) => {
    const newProject = { id: project.id, label: project.projectLabel };
    setSelectedProjects((prev) => {
      // Check if project already exists to avoid duplicates
      const exists = prev.some((p) => p.id === newProject.id);
      if (exists) {
        return prev; // Don't add if already exists
      }
      return [...prev, newProject]; // Append to current selection
    });
    setIsProjectSelectionOpen(false);
  };

  const handleProjectsSelect = (projects: ProjectRowType[]) => {
    const newProjects = projects.map((project) => ({ id: project.id, label: project.projectLabel }));
    setSelectedProjects(newProjects);
    setIsProjectSelectionOpen(false);
  };

  const handleProjectAccessSave = (projectAccess: ApiProjectAccessDto[]) => {
    handleInputChange('projects', projectAccess);
    setIsProjectAccessDialogOpen(false);
  };

  const handleMaterialCreate = async (materialRecord: CustomizingRecord) => {
    if (!backend) return;

    try {
      // Convert CustomizingRecord to MaterialDto
      // MaterialDto.id must be number | undefined, not string
      const materialDto: MaterialDto = {
        name: materialRecord.name as string | undefined,
        form: materialRecord.form as string | undefined,
        externalId: materialRecord.externalId as string | undefined,
        isSubstrate: materialRecord.isSubstrate as boolean | undefined,
        isConfidential: materialRecord.isConfidential as boolean | undefined,
        label: materialRecord.label as string | undefined,
        // Don't include id for new materials - it will be generated by the backend
      };

      // Create the material directly to get the saved material with ID
      const savedMaterial = await backend.material.create(materialDto);

      // Invalidate materials cache to refresh options
      invalidateCachedOptions(['materials']);

      // Close the material creation dialog
      setIsMaterialCreateDialogOpen(false);

      // FIXME: setTimeout(100ms) hack to wait for options refresh after cache invalidation — see docs/guide/effects-and-async-state.md
      // The useSelectOptions hook will automatically refresh due to cache invalidation
      setTimeout(() => {
        if (savedMaterial?.id) {
          handleInputChange('materialID', savedMaterial.id);
        }
      }, 100);
    } catch (error) {
      log.error('[CustomizingEntityDialog] Error creating material:', error);
    }
  };

  const renderField = (field: any) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];

    // Get options for this field
    const getOptions = () => {
      if (typeof field.options === 'string') {
        // Get options from the selectOptions system
        const optionData = selectOptions[field.options as OptionKey];
        if (Array.isArray(optionData)) {
          // Special handling for materials: store both name and label for proper searching/display
          if (field.options === 'materials') {
            return optionData
              .map((item: any) => ({
                value: item.id || item.key || item.value,
                label: item.label || item.name || item.value, // HTML label for display
                name: item.name || item.value || '', // Plain name for searching
                rawItem: item, // Store raw item for potential use
              }))
              .filter((option: any) => option.value && (option.label || option.name)); // Filter out invalid options
          }
          // Special handling for synthesisTechniques in users: only show top-level techniques (no parent or groups)
          if (field.options === 'synthesisTechniques' && entityConfig.title === 'Users') {
            // Filter to show only top-level techniques: those without a parent, or those that are groups
            const filtered = optionData.filter((item: any) => {
              const itemId = item.id || item.key || item.value;
              const hasParent = item.parentTechnique || item.parentID || item.parent;
              // If it has no parent, it's top-level
              if (!hasParent) return true;
              // If it's a group (has children - other techniques reference it as parent), also include it
              const isGroup = optionData.some((other: any) => {
                const otherParent = other.parentTechnique || other.parentID || other.parent;
                return otherParent === itemId;
              });
              return isGroup;
            });
            return filtered
              .map((item: any) => ({
                value: item.key || item.id || item.value,
                label: item.value || item.label || item.name,
              }))
              .filter((option: any) => option.value && option.label);
          }
          // Default mapping for other entity types
          return optionData
            .map((item: any) => ({
              value: item.key || item.id || item.value,
              label: item.value || item.label || item.name,
            }))
            .filter((option: any) => option.value && option.label); // Filter out invalid options
        }
        return [];
      }
      // No other option types currently supported
      return [];
    };

    const options = getOptions();
    const fieldDisabled =
      field.disabled || (isProjectTypeLockedForEdit && (field.name === 'code' || field.name === 'isInternal'));

    switch (field.type) {
      case 'boolean':
        return (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(e) => handleInputChange(field.name, e.target.checked)}
                  disabled={fieldDisabled}
                />
              }
              label={field.label}
            />
            {error ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', ml: 0.5 }}>
                {error}
              </Typography>
            ) : isProjectTypeLockedForEdit && field.name === 'isInternal' ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
                Cannot change while this type is used by projects.
              </Typography>
            ) : null}
          </Box>
        );

      case 'select':
        return (
          <FormSelect
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            label={field.label}
            disabled={fieldDisabled}
            required={field.required}
            options={options}
            error={!!error}
            helperText={error}
          />
        );

      case 'multiselect':
        return (
          <FormControl fullWidth error={!!error} size="medium">
            <InputLabel>
              <FormLabel required={field.required} optional={!field.required} isEditMode={true}>
                {field.label}
              </FormLabel>
            </InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
              disabled={fieldDisabled}
              size="medium"
            >
              {options.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'autocomplete':
        // Special handling for materials autocomplete: search by name, display label (HTML)
        const isMaterialAutocomplete = field.options === 'materials';
        const autocompleteComponent = (
          <FormAutocomplete
            options={options}
            value={value ? options.find((option: any) => option.value === value) || null : null}
            onChange={(_event, newValue) => handleInputChange(field.name, nullIfBlank(newValue?.value))}
            getOptionLabel={(option: any) => {
              // For materials, prefer name for the input text (plain text), but we'll render label in dropdown
              if (isMaterialAutocomplete && option.name) {
                return option.name;
              }
              return option.label || option;
            }}
            isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
            autoSelect
            autoHighlight
            filterOptions={
              isMaterialAutocomplete
                ? (opts, params) =>
                    filterAutocompleteOptionsByMatchRank(opts as any[], params, (option: any) =>
                      String(option.name ?? '')
                    )
                : undefined
            }
            renderOption={
              isMaterialAutocomplete
                ? (props, option: any) => (
                    <li {...props} key={option.value}>
                      {option.label ? (
                        <Box dangerouslySetInnerHTML={{ __html: option.label }} />
                      ) : (
                        option.name || option.label
                      )}
                    </li>
                  )
                : undefined
            }
            label={field.label}
            error={!!error}
            helperText={error}
            disabled={fieldDisabled}
            required={field.required}
          />
        );

        // Add "Create Material" button for materials autocomplete
        if (isMaterialAutocomplete) {
          return (
            <Box display="flex" gap={1} alignItems="flex-start">
              <Box flex={1}>{autocompleteComponent}</Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsMaterialCreateDialogOpen(true)}
                startIcon={<AddIcon />}
                sx={{ minWidth: 'auto', px: 1, height: '40px', mt: 0.5 }}
                title="Create new material"
              >
                Add
              </Button>
            </Box>
          );
        }

        return autocompleteComponent;

      case 'multiselect-autocomplete':
        return (
          <FormAutocomplete
            multiple
            options={options}
            value={
              Array.isArray(value)
                ? value.map((v: any) => options.find((option: any) => option.value === v)).filter(Boolean)
                : []
            }
            onChange={(_event, newValue) =>
              handleInputChange(
                field.name,
                newValue.map((item: any) => item.value)
              )
            }
            getOptionLabel={(option: any) => {
              if (typeof option === 'string') return option;
              if (typeof option === 'object' && option.label) return option.label;
              return String(option);
            }}
            isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
            label={field.label}
            error={!!error}
            helperText={error}
            disabled={fieldDisabled}
            required={field.required}
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip
                    key={tagProps.key}
                    variant="outlined"
                    label={option.label}
                    {...(tagProps as Omit<typeof tagProps, 'key'>)}
                  />
                );
              })
            }
          />
        );

      case 'project-select':
        return (
          <Box display="flex" gap={1} alignItems="flex-start">
            <FormAutocomplete
              fullWidth
              multiple
              options={[]} // We'll load projects separately
              getOptionLabel={(option) => option.label}
              value={selectedProjects}
              onChange={(_, newValue) => {
                setSelectedProjects(newValue);
                handleInputChange(
                  field.name,
                  newValue.map((p) => p.id)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              label={field.label}
              size="small"
              error={!!error}
              helperText={error}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  minHeight: '40px',
                  maxHeight: '40px',
                  overflow: 'hidden',
                },
                '& .MuiAutocomplete-tag': {
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiAutocomplete-input': {
                  padding: '8px 14px',
                },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsProjectSelectionOpen(true)}
              startIcon={<SearchIcon />}
              sx={{ minWidth: 'auto', px: 1, height: '40px' }}
              title="Search and select projects"
            >
              Lookup
            </Button>
          </Box>
        );

      case 'project-access':
        const projects = (formData[field.name] as ApiProjectAccessDto[]) || [];
        const projectAccessSummary =
          projects.length > 0 ? projects.map((p) => p.projectLabel).join(', ') : 'No projects';

        return (
          <Box display="flex" gap={1} alignItems="flex-start">
            <FormTextField
              fullWidth
              label={field.label}
              value={projectAccessSummary}
              InputProps={{ readOnly: true }}
              size="small"
              error={!!error}
              helperText={error}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsProjectAccessDialogOpen(true)}
              sx={{ height: '40px' }}
              title="Manage project access"
            >
              Manage
            </Button>
          </Box>
        );

      case 'date':
      case 'datetime-local':
        return (
          <FormTextField
            fullWidth
            type={field.type}
            label={field.label}
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            error={!!error}
            helperText={error}
            disabled={fieldDisabled}
            required={field.required}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case 'richtext':
        return (
          <FormTextField
            fullWidth
            multiline
            rows={4}
            label={field.label}
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            error={!!error}
            helperText={error}
            disabled={fieldDisabled}
            placeholder={field.placeholder}
            required={field.required}
            sx={{
              '& .MuiInputBase-input': {
                whiteSpace: 'pre-wrap',
              },
            }}
          />
        );

      case 'instrument-file-metadata': {
        const instrumentId = String(formData.id ?? '');
        if (!instrumentMetadataKind) {
          return null;
        }
        const metadataConfig =
          (formData.metadataConfig as FileMetaDataConfigDto | undefined) ??
          createEmptyInstrumentMetadataConfig(instrumentMetadataKind, instrumentId);
        return (
          <InstrumentFileMetadataConfigEditor
            config={metadataConfig}
            onChange={(next) => handleInputChange('metadataConfig', next)}
            canEdit={canEditFileMetadata}
          />
        );
      }

      default: {
        const isProjectTypePrefixWhenUsed = entityType === 'projectTypes' && record?.isUsed && field.name === 'prefix';
        const isProjectTypeCodeWhenUsed = isProjectTypeLockedForEdit && field.name === 'code';
        return (
          <FormTextField
            fullWidth
            type={field.type}
            label={field.label}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            error={!!error}
            helperText={
              error ||
              (isProjectTypeCodeWhenUsed
                ? 'Cannot change while this type is used by projects.'
                : isProjectTypePrefixWhenUsed
                  ? 'When in use: you may only add new prefixes (comma-separated), not remove existing ones.'
                  : undefined)
            }
            disabled={fieldDisabled}
            placeholder={field.placeholder}
            required={field.required}
          />
        );
      }
    }
  };

  // Add label preview for materials
  const renderLabelPreview = () => {
    if (entityConfig.title === 'Materials' && formData.name) {
      const generatedLabel = generateMaterialLabel(formData.name);
      return (
        <Box sx={{ gridColumn: '1 / -1', mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ mb: 1, fontWeight: 'bold' }}>Generated Label Preview:</Box>
          <Box
            dangerouslySetInnerHTML={{ __html: generatedLabel }}
            sx={{
              fontSize: '1.1em',
              fontFamily: 'monospace',
              p: 1,
              bgcolor: 'white',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
            }}
          />
        </Box>
      );
    }
    return null;
  };

  const handleClose = (_event?: {}, _reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    // Safe to close
    setFormData({} as CustomizingRecord);
    setErrors({});
    setSelectedProjects([]);
    onClose();
  };

  const handleConfirmClose = () => {
    setShowUnsavedChangesDialog(false);
    setFormData({} as CustomizingRecord);
    setErrors({});
    setSelectedProjects([]);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={instrumentMetadataKind ? 'lg' : 'md'}
        fullWidth
        disableEscapeKeyDown={hasUnsavedChanges}
        slotProps={{
          backdrop: {
            onClick: hasUnsavedChanges ? undefined : onClose,
          },
        }}
      >
        <DialogTitle>{isEdit ? `Edit ${entityConfig.title}` : `Add New ${entityConfig.title}`}</DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!optionsLoaded || selectOptionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Typography>Loading...</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                {entityConfig.fields.map((field) => (
                  <Box
                    key={field.name}
                    sx={
                      field.name === 'description' || field.type === 'instrument-file-metadata'
                        ? { gridColumn: '1 / -1' }
                        : {}
                    }
                  >
                    {renderField(field)}
                  </Box>
                ))}
                {renderLabelPreview()}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Selection Dialog */}
      <ProjectSelectionDialog
        open={isProjectSelectionOpen}
        title="Select Projects"
        onProjectSelect={handleProjectSelect}
        onProjectsSelect={handleProjectsSelect}
        onClose={() => setIsProjectSelectionOpen(false)}
        allowMultiple={true}
      />

      {/* Project Access Dialog */}
      <ProjectAccessDialog
        key={formData.id + formData.projects?.length}
        open={isProjectAccessDialogOpen}
        projectAccess={formData.projects || []}
        onSave={handleProjectAccessSave}
        onClose={() => setIsProjectAccessDialogOpen(false)}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={showUnsavedChangesDialog} onClose={() => setShowUnsavedChangesDialog(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>You have unsaved changes. Are you sure you want to discard them?</DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedChangesDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmClose} color="primary">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Material Creation Dialog */}
      {isMaterialCreateDialogOpen ? (
        <CustomizingEntityDialog
          open={isMaterialCreateDialogOpen}
          entityType="materials"
          entityConfig={ENTITY_CONFIGS.materials}
          record={null}
          isEdit={false}
          onSave={handleMaterialCreate}
          onClose={() => setIsMaterialCreateDialogOpen(false)}
        />
      ) : null}
    </>
  );
}
